import { LevelPhase } from "@highmarch/domain";
import { audio } from "../audio/audio";
import { PauseOverlay, whenRemoved } from "../ui/pauseOverlay";
import type { LevelParts } from "./levelParts";
import { PauseCause, PauseState } from "./pauseState";

/** What the pause reaches outside the level's parts: the game's hooks and the frame loop. */
export interface PauseHooks {
  /** Opens the settings sheet (the game's), returning it; nothing when the game has none. */
  settings(): HTMLElement | void;
  /** The sim runs (true) or is held / over (false), for the screen wake lock. */
  awake(on: boolean): void;
  /** The sim is about to run again: the frame loop drops the time that passed while it was held. */
  resuming(): void;
}

/**
 * The level's pause: the HUD button, the page going to the background, the Settings sheets and the guide's card each
 * hold the sim for their own reason (PauseState), and this applies the sum to the clock, the paused layer, the music
 * and the wake lock.
 */
export class LevelPause {
  readonly state = new PauseState();
  private readonly layer: PauseOverlay;
  /** Stops watching the open Settings sheets (dispose). */
  private readonly sheetWatches: (() => void)[] = [];

  constructor(private readonly p: LevelParts, private readonly hooks: PauseHooks) {
    this.layer = new PauseOverlay(p.uiRoot, p.hud.root, () => { this.state.resume(); this.sync(); });
  }

  /** The HUD's pause button: pauses, or resumes a pause the player can lift. */
  toggle(): void { this.state.toggle(); this.sync(); this.p.raise("pauseToggled"); }

  /**
   * The page went to the background (main.ts: visibilitychange → hidden, pagehide). The level stays paused when the
   * player comes back, under the paused layer, rather than carrying on the instant the page is visible again.
   */
  autoPause(): void {
    // The opening draft has no clock running (the sim idles until START WAVES): a paused layer there would only cost
    // the player a tap before the next placement.
    if (this.p.ended || this.p.disposed || this.p.sim.phase === LevelPhase.Ready) return;
    this.hold("background");
  }

  /** A resumed battle waits under the paused layer until the player taps: nothing moves before they have looked. */
  holdResumed(): void {
    // the opening draft has no clock to hold (autoPause): the board simply waits for the next card or START WAVES
    const phase = this.p.sim.phase;
    if (phase === LevelPhase.OpeningDraft || phase === LevelPhase.Ready) return;
    this.hold("background");
  }

  /** Holds (on) or lets go (off) for the guide's card. */
  guideHold(on: boolean): void { if (on) this.state.hold("guide"); else this.state.release("guide"); this.sync(); }

  private hold(cause: PauseCause): void { this.state.hold(cause); this.sync(); }

  /** The HUD gear: Settings (and Graphics over it) pause the level while open; closing resumes only that pause. */
  openSettings(): void {
    const sheet = this.hooks.settings();
    if (!sheet || this.p.ended) return;
    this.state.sheetOpened();
    this.sync();
    this.sheetWatches.push(whenRemoved(sheet, this.p.uiRoot, () => { this.state.sheetClosed(); this.sync(); }));
  }

  /** A tap on the map while the paused layer is up resumes instead of acting on the board. */
  tapResumes(): boolean {
    if (!this.layer.shown) return false;
    this.state.resume();
    this.sync();
    return true;
  }

  /** The run is over: the end screen is not a pause — no layer, no duck — and the wake lock lets go. */
  clear(): void { this.state.clear(); this.sync(); }

  /** Applies the pause state to the clock, the HUD button, the paused layer, the music and the wake lock. */
  sync(): void {
    const live = !this.p.ended && !this.p.disposed;
    const paused = this.state.paused && live;
    // no frame's worth of stale time reaches the sim on resume (the loop's own dt cap is the second guard)
    if (this.p.clock.paused && !paused) this.hooks.resuming();
    this.p.clock.setPaused(paused);
    this.p.hud.setPaused(this.state.showsLayer && live);
    this.layer.show(this.state.showsLayer && live);
    // The guide's card holds the sim but is being read: the music stays up and the screen stays awake.
    const onlyGuide = this.state.onlyGuide;
    audio.pauseDuck(paused && !onlyGuide);
    this.hooks.awake(live && (!paused || onlyGuide));
  }

  /** After the level is marked disposed: un-ducks the music, lets the wake lock go and removes the layer. */
  dispose(): void {
    for (const stop of this.sheetWatches) stop();
    this.sync();
    this.layer.dispose();
  }
}
